import { pricingConfig } from '../data/pricing';
import type { BundleId } from '../data/pricing';
import { shopifyConfig } from '../data/shopify';
import { getBundle, priceBundle } from './pricing';

/** One official Shopify system and how many complete copies of it are in the order. */
export type OrderLine = { bundleId: BundleId; qty: number };

const ATTR_KEY = 'mfl-attribution';
const cents = (n: number) => Math.round(n * 100);
const fromCents = (c: number) => c / 100;

/** Merges duplicate systems, drops empty lines, largest systems first. */
export function normalizeOrder(lines: OrderLine[]): OrderLine[] {
  const map = new Map<BundleId, number>();
  for (const l of lines) if (l.qty > 0) map.set(l.bundleId, (map.get(l.bundleId) ?? 0) + l.qty);
  return Array.from(map, ([bundleId, qty]) => ({ bundleId, qty })).sort(
    (a, b) => getBundle(b.bundleId).qty - getBundle(a.bundleId).qty,
  );
}

export function orderTotals(lines: OrderLine[]) {
  let lights = 0;
  let total = 0;
  for (const l of lines) {
    const b = getBundle(l.bundleId);
    lights += b.qty * l.qty;
    total += cents(priceBundle(b).total) * l.qty;
  }
  const list = cents(pricingConfig.unitPrice) * lights;
  return { lights, total: fromCents(total), list: fromCents(list), savings: fromCents(list - total) };
}

/** Cheapest combination of official systems giving exactly `lights` M1 PRO units. */
export function optimalForLights(lights: number): { lines: OrderLine[]; total: number } {
  const bundles = pricingConfig.bundles.map((b) => ({ id: b.id, qty: b.qty, c: cents(priceBundle(b).total) }));
  const cost = new Array<number>(lights + 1).fill(Infinity);
  const pick = new Array<BundleId | null>(lights + 1).fill(null);
  cost[0] = 0;
  for (let i = 1; i <= lights; i++) {
    for (const b of bundles) {
      if (b.qty > i) continue;
      const c = cost[i - b.qty] + b.c;
      // Strictly cheaper wins; on a tie prefer the larger (fewer packages) system.
      if (c < cost[i] || (c === cost[i] && pick[i] && b.qty > getBundle(pick[i] as BundleId).qty)) {
        cost[i] = c;
        pick[i] = b.id;
      }
    }
  }
  const out: OrderLine[] = [];
  let i = lights;
  while (i > 0 && pick[i]) {
    const id = pick[i] as BundleId;
    out.push({ bundleId: id, qty: 1 });
    i -= getBundle(id).qty;
  }
  return { lines: normalizeOrder(out), total: fromCents(cost[lights]) };
}

/** Smart System Savings: same number of lights for less, using official prices only. */
export function smartSavings(lines: OrderLine[]) {
  const current = orderTotals(lines);
  if (current.lights < 2) return null;
  const best = optimalForLights(current.lights);
  const saves = fromCents(cents(current.total) - cents(best.total));
  if (saves < 0.01) return null;
  return { lines: best.lines, total: best.total, saves, lights: current.lights };
}

/** Remember ad attribution from the landing URL so SPA navigation can't drop it. */
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  const found: Record<string, string> = {};
  for (const k of shopifyConfig.attributionParams) {
    const v = params.get(k);
    if (v) found[k] = v;
  }
  if (Object.keys(found).length) {
    try {
      sessionStorage.setItem(ATTR_KEY, JSON.stringify(found));
    } catch {
      /* storage unavailable */
    }
  }
}

export function attributionQuery() {
  const out = new URLSearchParams();
  let stored: Record<string, string> = {};
  try {
    stored = JSON.parse(sessionStorage.getItem(ATTR_KEY) || '{}');
  } catch {
    stored = {};
  }
  const live = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  for (const k of shopifyConfig.attributionParams) {
    const v = live.get(k) || stored[k];
    if (v) out.set(k, v);
  }
  return out.toString();
}

/**
 * Shopify cart permalink from the final confirmed order.
 * `variant:qty` — qty is the number of COMPLETE systems, not lights.
 */
export function buildCheckoutUrl(lines: OrderLine[], withAttribution = true) {
  const parts = normalizeOrder(lines).map((l) => `${getBundle(l.bundleId).variantId}:${l.qty}`);
  const base = `https://${shopifyConfig.storeDomain}/cart/${parts.join(',')}`;
  const q = withAttribution ? attributionQuery() : '';
  return q ? `${base}?${q}` : base;
}

/** Meta-ready contents for AddToCart / InitiateCheckout. */
export function metaContents(lines: OrderLine[]) {
  return normalizeOrder(lines).map((l) => {
    const b = getBundle(l.bundleId);
    // Shopify variant ID — the same content ID Shopify/Omega sends with Purchase.
    return { id: b.variantId, quantity: l.qty, item_price: priceBundle(b).total };
  });
}

/** Recommended installed / ready split for a system. */
export function setupFor(id: BundleId) {
  const q = getBundle(id).qty;
  if (q < 2) return null;
  return { installed: q / 2, ready: q / 2 };
}
